import { IPromise, IQService, IRootScopeService } from 'angular';
import {
    EMPTY_TOTALS,
    FontStyleService,
    ICartFilters,
    IClientCart,
    ILicenseUpgradeDict,
    ITotals,
    filterCart,
} from '../../tn.common';
import { Pricing } from '../../tn.pricing/service.tn.pricing';
import { UserService } from '../../tn.user/service.tn.user';
import { CartRepository } from '../cart-repository.service/cartRepository';

/*
 *  LocalCarts stores a collection of the users carts locally that controllers can bind to
 *  This allows the nav-bar and carts page to stay in sync.
 */
export class LocalCarts {
    filters: ICartFilters = {};
    carts: IClientCart[] = [];
    filteredCarts: IClientCart[] = [];

    static $inject = [
        'cartRepository',
        '$rootScope',
        'tn.pricing.service',
        '$q',
        'tn.user.service',
        'FontStyleService',
    ];
    constructor(
        protected cartRepository: CartRepository,
        protected $rootScope: IRootScopeService,
        protected pricingService: Pricing,
        protected $q: IQService,
        protected userService: UserService,
        protected styleService: FontStyleService
    ) {
        if (userService.isLoggedIn()) {
            this._updateCarts();
        }
        this.userService.login$.subscribe(() => this._updateCarts());
        this.userService.logout$.subscribe(() => {
            // wipe out the stored carts on logout.
            this.carts.splice(0, this.carts.length);
            this.filteredCarts.splice(0, this.filteredCarts.length);
        });
    }

    getCart(id: number): IPromise<IClientCart> {
        return this.cartRepository.getCart(id);
    }

    getCarts(): IClientCart[] {
        return this.carts;
    }

    deleteCart(cart: IClientCart): IPromise<void> {
        return this.cartRepository
            .deleteCart(cart)
            .then(() => this._updateCarts());
    }

    saveCart(cart: IClientCart): IPromise<IClientCart> {
        // TODO: Should we check cart.id here, and redirect to updateCart?

        if (!cart.date) {
            cart.date = new Date();
        }

        return this.cartRepository.saveCart(cart).then((_cart) => {
            this._updateCarts();
            return _cart;
        });
    }

    updateCart(cart: IClientCart): IPromise<IClientCart> {
        return this.cartRepository.updateCart(cart).then((savedCart) => {
            this._updateCarts();
            return savedCart;
        });
    }

    setFilteredCarts(filters: ICartFilters): void {
        this.filters = filters;
        const enabled = Object.keys(this.filters).length > 0;
        const filteredCarts = enabled
            ? this.carts.filter((cart) => filterCart(cart, filters))
            : this.carts;

        // Empty current collection, then insert new styles
        this.filteredCarts.splice(0, this.filteredCarts.length);

        filteredCarts.forEach((cart) => {
            this.recalculateTotals(cart).then((cartData) =>
                this.filteredCarts.push(cartData['cart'])
            );
        });
    }

    getFilteredCarts(): IClientCart[] {
        return this.filteredCarts;
    }

    _updateCarts(): IPromise<void> {
        return this.cartRepository.listCarts().then((carts) => {
            this.carts.splice(0, this.carts.length);
            // when carts come from the repository, they do not have pricing, so we need to add it.
            const promises = carts.map((cart) =>
                this._updateCartPricing(cart).then((cart) =>
                    this.carts.push(cart)
                )
            );
            // apply filter after all carts have been updated, so were churning the ui.
            this.$q
                .all(promises)
                .then(() => this.setFilteredCarts(this.filters));
        });
    }

    _updateCartPricing(cart: IClientCart): IPromise<IClientCart> {
        // ensure the individual license prices are up to date with the server,
        // then calculate the subtotal.
        return this._updateLicensePrices(cart).then((cart) =>
            this.pricingService
                .calculateSubTotal(cart.license)
                .then((subtotal) => {
                    cart.subtotal = subtotal;
                    return cart;
                })
        );
    }

    // Styles in the cart can get stale. We store them as json in the db so the model
    // there doesn't necessarily get updated.  The styles can also be altered in the db
    // between adding them to the cart and checking out which can cause price
    // mismatches at checkout.
    // TODO: update the styles on load.
    // TODO: move this functionality to CartService
    // This function updates the styles in the cart to match the styles in the db,
    // it does it in place.
    _updateLicensePrices(cartData: IClientCart): IPromise<IClientCart> {
        if (!cartData.license) {
            return this.$q.resolve(cartData);
        }
        const ids = cartData.license.map(({ font }) => font.id);

        return this.styleService
            .list({ id: ids, page_size: ids.length + 10 })
            .then(({ results: styles }) => {
                const styleIdx = styles.reduce((idx, style) => {
                    idx[style.id] = style;
                    return idx;
                }, {});
                cartData.license.forEach((license, index) => {
                    const style = styleIdx[license.font.id];
                    if (!style) return;
                    cartData.license[index].font.base_price = style.base_price;
                    cartData.license[index].font.bucket = style.bucket;
                });
                return cartData;
            });
    }

    recalculateTotals(
        cartData: IClientCart,
        cartTotals: ITotals = EMPTY_TOTALS,
        licensesUpgradeDict: ILicenseUpgradeDict = {},
        licenseCosts: {
            [key: number]: { upgrade: number; nonUpgrade: number };
        } = {}
    ): IPromise<{
        cart: IClientCart;
        cartTotals: ITotals;
        licensesUpgradeDict: ILicenseUpgradeDict;
        licenseCosts: {
            [key: number]: { upgrade: number; nonUpgrade: number };
        };
    }> {
        return this._updateLicensePrices(cartData).then(() => {
            const calculateTotalPromise = this.pricingService
                .calculateTotals(
                    cartData.license || [],
                    cartData.coupon,
                    licensesUpgradeDict
                )
                .then((totals) => {
                    cartTotals.oneTime = totals.oneTime;
                    cartTotals.annual = totals.annual;
                    cartTotals.threeYears = totals.threeYears;
                    cartTotals.fiveYears = totals.fiveYears;
                    cartTotals.subtotal = totals.subtotal;
                    cartTotals.coupon = totals.coupon;
                    cartTotals.finals = totals.finals;
                });

            const calculatePricesPromise = this.pricingService
                .calculateLicensePrices(cartData.license || [])
                .then((costs) => {
                    costs.forEach((licensePrice) => {
                        const license = cartData.license.filter(
                            (li) => li.font.id === licensePrice.fontId
                        )[0];
                        const upgrade = Object.keys(license.fontRights).reduce(
                            (total, key) => {
                                if (
                                    license.fontRights[key].upgrade ||
                                    license.fontRights[key].renew
                                ) {
                                    return (
                                        total + licensePrice[key + 'Price'].base
                                    );
                                }
                                return total;
                            },
                            0
                        );
                        const nonUpgrade = Object.keys(
                            license.fontRights
                        ).reduce((total, key) => {
                            if (
                                !license.fontRights[key].upgrade &&
                                !license.fontRights[key].renew
                            ) {
                                return total + licensePrice[key + 'Price'].base;
                            }
                            return total;
                        }, 0);
                        licenseCosts[licensePrice.fontId] = {
                            upgrade,
                            nonUpgrade,
                        };
                    });
                });

            return this.$q
                .all([calculateTotalPromise, calculatePricesPromise])
                .then(() => ({
                    cart: cartData,
                    cartTotals: cartTotals,
                    licensesUpgradeDict,
                    licenseCosts: licenseCosts,
                }));
        });
    }
}
